import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {interval, ReplaySubject} from "rxjs";
import {UserPermissions} from "../../../shared/interface/user-permissions";
import {MatDialog} from "@angular/material/dialog";
import {MatSnackBar} from "@angular/material/snack-bar";
import {AuthService} from "../../../shared/services/auth.service";
import {FilterService} from "../../../shared/services/filter.service";
import {ActivatedRoute, Router} from "@angular/router";
import {select, Store} from "@ngrx/store";
import {StoreRootState} from "../../../shared/ngrx/reducers";
import {CompetitionImportService} from "../../../shared/services/competition-import.service";
import {delay, distinctUntilChanged, takeUntil, tap} from "rxjs/operators";
import {CountedAndPaginated} from "../../../shared/interface/response/counted-and-paginated";
import {getCurrentRouteState} from "../../../shared/ngrx/selector";
import {RouterStateUrl} from "../../../shared/ngrx/route-serializer";
import {CompetitionImport} from "../../../shared/interface/competition-import";
import {NgForm} from "@angular/forms";
import {IMPORT_STATUS_IN_PROGRESS, IMPORT_STATUS_IN_CONFIGURATION} from "../../../shared/services/dict.service";
import {ConfirmDialogComponent} from "../../../shared/components/confirm-dialog/confirm-dialog.component";
import {Uuid} from "../../../shared/interface/response/uuid";
import {JobResultDialogComponent} from "../../../shared/components/job-result-dialog/job-result-dialog.component";
import {HttpErrorResponse} from "@angular/common/http";


@Component({
  selector: 'app-competition-import-list',
  templateUrl: './competition-import-list.component.html',
  styleUrls: ['./competition-import-list.component.scss']
})
export class CompetitionImportListComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('filtersForm') filtersForm: NgForm;

  initialFilters: any;
  displayedColumns: string[] = [
    'name',
    'importDate',
    'createdBy',
    'importRow',
    'competitionName',
    'status',
    'actions'
  ];

  sortBy = null;
  sortOrder = null;
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;
  imports: CompetitionImport[] = [];
  progressBarDelete: string;
  readonly IMPORT_STATUS_IN_PROGRESS = IMPORT_STATUS_IN_PROGRESS;
  readonly IMPORT_STATUS_IN_CONFIGURATION = IMPORT_STATUS_IN_CONFIGURATION;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private competitionImportService: CompetitionImportService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private auth: AuthService,
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private store: Store<StoreRootState>,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.competitionImportService.importListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<CompetitionImport>) => {
          this.imports = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      contains$name: 'string',
      contains$competitionName: 'string',
      status: 'int',
      gte$createdAt: 'string',
      lte$createdAt: 'string',
      createdBy: 'string',
    }, filters);
  }

  ngOnInit(): void {
    interval(5000)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        if (this.paginator.pageIndex === 0) {
          this.loadListData();
        }
      });
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path,
        this.filterService.parseFiltersToQueryParams({
          page: event.pageIndex,
          pageSize: event.pageSize,
          ...this.filtersForm.value
        })
      ]);
    });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.loadListData();
      })
    ;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadListData(): void {
    this.competitionImportService.loadImportList(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.initialFilters
    );
  }


  filter(): void {
    const formValues = { ...this.filtersForm.value };

    for (const key in formValues) {
      if (formValues[key] === undefined || formValues[key] === null || formValues[key] === '') {
        delete formValues[key];
      }
    }

    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path,
      this.filterService.parseFiltersToQueryParams({
        page: 0,
        pageSize: this.paginator.pageSize,
        ...formValues
      })
    ]);
  }

  backToConfiguration(competitionImport: CompetitionImport): void {
    this.router.navigate(['/wyniki-konkursow/import'], {
      queryParams: { importId: competitionImport.uuid }
    });
  }


  downloadImportFile(competitionImport: CompetitionImport): void {
    this.competitionImportService.downloadImportFile(competitionImport.uuid);
  }

  deleteImport(competitionImport: CompetitionImport): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć import?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.progressBarDelete = competitionImport.uuid;
        this.competitionImportService.deleteImport(competitionImport.uuid)
          .subscribe((response: Uuid) => {
            const dialogRefv1 = this.dialog.open(JobResultDialogComponent, {
              disableClose: true,
              width: '500px',
              data: {jobId: response.uuid, showProgress: false},
            });

            dialogRefv1.afterClosed().subscribe(() => {
                this.snack.open('Pomyślnie usunięto import', '', {
                  duration: 2000,
                });
                this.loadListData();
              }
            );
          }, (error: HttpErrorResponse) => {
            this.snack.open('Przy usuwaniu pliku wystąpił błąd', error.status.toString(), {duration: 4000});
            if (error.status === 400) {
              this.snack.open(error.error.error.text, '400', {duration: 4000});
            }
          });
      }
    });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();

    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }


}
